import { waitForPromise } from "@ember/test-waiters";
import { isRailsTesting, isTesting } from "discourse/lib/environment";
import { helperContext } from "discourse/lib/helpers";
import { SELECTORS } from "discourse/lib/lightbox/constants";
import quoteImage, {
  canBuildImageQuote,
} from "discourse/lib/lightbox/quote-image";
import { isDocumentRTL } from "discourse/lib/text-direction";
import { escapeExpression } from "discourse/lib/utilities";
import { i18n } from "discourse-i18n";

const INIT_PROMISES = new WeakMap();

export function sortLightboxItems(items) {
  const result = [];
  let i = 0;
  while (i < items.length) {
    const grid = items[i].closest(".d-image-grid");
    if (!grid) {
      result.push(items[i++]);
      continue;
    }
    let j = i;
    while (j < items.length && items[j].closest(".d-image-grid") === grid) {
      j++;
    }
    const gridItems = items.slice(i, j);
    const sorted = gridItems.every((item) => {
      const position = item.dataset.lightboxPosition;
      return (
        position !== undefined &&
        position !== "" &&
        Number.isInteger(Number(position))
      );
    })
      ? gridItems.sort(
          (a, b) =>
            Number(a.dataset.lightboxPosition) -
            Number(b.dataset.lightboxPosition)
        )
      : gridItems;
    result.push(...sorted);
    i = j;
  }
  return result;
}

export default function lightbox(elem, additionalData = {}) {
  if (!elem) {
    return Promise.resolve();
  }

  if (INIT_PROMISES.has(elem)) {
    return INIT_PROMISES.get(elem);
  }

  const promise = waitForPromise(initLightbox(elem, additionalData)).catch(
    (error) => {
      INIT_PROMISES.delete(elem);
      throw error;
    }
  );

  INIT_PROMISES.set(elem, promise);
  return promise;
}

async function initLightbox(elem, additionalData = {}) {
  const currentUser = helperContext()?.currentUser;
  const siteSettings = helperContext().siteSettings;
  const caps = helperContext().capabilities;

  const { default: PhotoSwipeLightbox } = await waitForPromise(
    import(/* dynamicChunkName: "photoswipe-lightbox" */ "photoswipe/lightbox")
  );
  const canDownload =
    !siteSettings.prevent_anons_from_downloading_files || !!currentUser;
  const canQuoteImage = !!currentUser;
  const rtl = isDocumentRTL();
  const items = sortLightboxItems([
    ...elem.querySelectorAll(SELECTORS.DEFAULT_ITEM_SELECTOR),
  ]);

  if (rtl) {
    items.reverse();
  }

  items.forEach((el, index) => {
    el.addEventListener("click", (e) => {
      e.preventDefault();

      lightboxEl.loadAndOpen(index);
    });
  });

  const lightboxEl = new PhotoSwipeLightbox({
    ...sharedOptions(() => lightboxEl.pswp),
    dataSource: items,
    counter: false,
    paddingFn,
  });

  closeOnEscape(lightboxEl);

  lightboxEl.on("afterInit", () => {
    const el = lightboxEl.pswp.currSlide.data.element;
    el.querySelector(".meta")?.classList.add("open");
  });

  lightboxEl.on("close", function () {
    lightboxEl.pswp.element.classList.add("pswp--behind-header");
  });

  lightboxEl.on("destroy", () => {
    const el = lightboxEl.pswp.currSlide.data.element;
    el.querySelector(".meta")?.classList.remove("open");
  });

  lightboxEl.on("uiRegister", function () {
    lightboxEl.pswp.ui.registerElement({
      name: "caption",
      order: 11,
      isButton: false,
      appendTo: "root",
      html: "",
      onInit: (caption, pswp) => {
        pswp.on("change", () => {
          const { aiDescription, element, title, inlineSVG } =
            pswp.currSlide.data;

          if (!element || inlineSVG) {
            caption.innerHTML = "";
            return;
          }

          const captionTitle = title ? escapeExpression(title) : null;
          const captionAiDescription = aiDescription
            ? escapeExpression(aiDescription)
            : null;
          const captionDetails =
            element.querySelector(".informations")?.textContent;
          const titleEl = captionTitle
            ? `<div class='pswp__caption-title'>${captionTitle}</div>`
            : null;
          const aiDescriptionEl = captionAiDescription
            ? `<div class='pswp__caption-ai-description'>${captionAiDescription}</div>`
            : null;
          const detailsEl = captionDetails
            ? `<div class='pswp__caption-details'>${captionDetails}</div>`
            : null;

          caption.innerHTML = [titleEl, aiDescriptionEl, detailsEl]
            .filter(Boolean)
            .join("");
        });
      },
    });

    if (canDownload) {
      lightboxEl.pswp.ui.registerElement({
        name: "download-image",
        order: 7,
        isButton: true,
        tagName: "a",
        title: i18n("lightbox.download"),
        html: {
          isCustomSVG: true,
          inner:
            '<path d="M20.5 14.3 17.1 18V10h-2.2v7.9l-3.4-3.6L10 16l6 6.1 6-6.1ZM23 23H9v2h14Z" id="pswp__icn-download"/>',
          outlineID: "pswp__icn-download",
        },
        onInit: (el, pswp) => {
          el.setAttribute("download", "");
          el.setAttribute("target", "_blank");
          el.setAttribute("rel", "noopener");

          pswp.on("change", () => {
            const href = pswp.currSlide.data.element.dataset.downloadHref;
            if (!href) {
              el.style.display = "none";
              return;
            }
            el.href = href;
          });
        },
      });
    }

    lightboxEl.pswp.ui.registerElement({
      name: "original-image",
      order: 8,
      isButton: true,
      tagName: "a",
      title: i18n("lightbox.open"),
      html: {
        isCustomSVG: true,
        inner:
          '<path id="pswp__icn-image" d="M8 4C6.23 4 4.8 5.43 4.8 7.2v17.6c0 1.77 1.43 3.2 3.2 3.2h17.6c1.77 0 3.2-1.43 3.2-3.2V7.2c0-1.77-1.43-3.2-3.2-3.2H8zm2.56 4C11.6 8 12.8 9.2 12.8 10.8s-1.2 2.8-2.8 2.8-2.8-1.2-2.8-2.8S9.04 8 10.56 8zm7.2 4.48c0.42 0 0.84 0.21 1.1 0.56l5.44 8.72c0.28 0.46 0.29 1.04 0.03 1.52s-0.74 0.72-1.28 0.72H9.2a1.36 1.36 0 0 1-1.3-0.88c-0.26-0.52-0.22-1.12 0.12-1.6l3.12-4.64c0.28-0.4 0.74-0.64 1.24-0.64s0.96 0.24 1.24 0.64l1.64 2.32 3.76-6.16a1.28 1.28 0 0 1 1.1-0.56z"/>',
        outlineID: "pswp__icn-image",
      },
      onInit: (el, pswp) => {
        el.setAttribute("target", "_blank");
        el.setAttribute("rel", "noopener");

        pswp.on("change", () => {
          el.href = pswp.currSlide.data.src;
        });
      },
    });

    lightboxEl.pswp.ui.registerElement({
      name: "image-info",
      order: 9,
      isButton: true,
      tagName: "a",
      title: i18n("lightbox.image_info"),
      html: {
        isCustomSVG: true,
        inner:
          '<path id="pswp__icn-info" d="M16 28.8C23.07 28.8 28.8 23.07 28.8 16C28.8 8.93 23.07 3.2 16 3.2C8.93 3.2 3.2 8.93 3.2 16C3.2 23.07 8.93 28.8 16 28.8zM14.4 11.2C14.4 10.31 15.115 9.6 16 9.6C16.88 9.6 17.6 10.315 17.6 11.2C17.6 12.085 16.885 12.8 16 12.8C15.115 12.8 14.4 12.085 14.4 11.2zM14 14.4L16.4 14.4C17.065 14.4 17.6 14.935 17.6 15.6L17.6 20L18 20C18.665 20 19.2 20.535 19.2 21.2C19.2 21.865 18.665 22.4 18 22.4L14 22.4C13.335 22.4 12.8 21.865 12.8 21.2C12.8 20.535 13.335 20 14 20L15.2 20L15.2 16.8L14 16.8C13.335 16.8 12.8 16.265 12.8 15.6C12.8 14.935 13.335 14.4 14 14.4z"/>',
        outlineID: "pswp__icn-info",
      },
      onInit: (el, pswp) => {
        pswp.on("change", () => {
          el.style.display = pswp.currSlide.data.details ? "block" : "none";
        });
      },
      onClick: () => {
        lightboxEl.pswp.element.classList.toggle("pswp--caption-expanded");
      },
    });

    if (canQuoteImage) {
      lightboxEl.pswp.ui.registerElement({
        name: "quote-image",
        order: 10,
        isButton: true,
        title: i18n("lightbox.quote"),
        html: {
          isCustomSVG: true,
          inner:
            '<path id="pswp__icn-quote" d="M2 13.5C2 9.356 5.356 6 9.5 6L10 6C11.11 6 12 6.894 12 8C12 9.106 11.11 10 10 10L9.5 10C7.569 10 6 11.57 6 13.5L6 14L10 14C12.21 14 14 15.79 14 18L14 22C14 24.21 12.21 26 10 26L6 26C3.794 26 2 24.21 2 22L2 20L2 18L2 13.5M18 13.5C18 9.356 21.36 6 25.5 6L26 6C27.11 6 28 6.894 28 8C28 9.106 27.11 10 26 10L25.5 10C23.57 10 22 11.57 22 13.5L22 14L26 14C28.21 14 30 15.79 30 18L30 22C30 24.21 28.21 26 26 26L22 26C19.79 26 18 24.21 18 22L18 20L18 18L18 13.5"/>',
          outlineID: "pswp__icn-quote",
        },
        onInit: (el, pswp) => {
          pswp.on("change", () => {
            const slideData = pswp.currSlide?.data;
            const slideElement = slideData?.element;
            el.style.display = canBuildImageQuote(slideElement, slideData)
              ? ""
              : "none";
          });
        },
        onClick: () => {
          const slideData = lightboxEl.pswp.currSlide?.data;
          const slideElement = slideData?.element;
          quoteImage(slideElement, slideData).then((didQuote) => {
            if (didQuote) {
              lightboxEl.pswp.close();
            }
          });
        },
      });
    }

    lightboxEl.pswp.ui.registerElement({
      name: "custom-counter",
      order: 6,
      isButton: false,
      appendTo: "bar",
      onInit: (el, pswp) => {
        pswp.on("change", () => {
          const total = pswp.getNumItems();
          const index = rtl ? total - pswp.currIndex : pswp.currIndex + 1;
          el.textContent = `${index} / ${total}`;
        });
      },
    });
  });

  lightboxEl.addFilter("itemData", (data) => {
    const el = data.element;

    if (!el) {
      return data;
    }

    // use data attributes for width/height when available
    let width = el.getAttribute("data-target-width");
    let height = el.getAttribute("data-target-height");
    const isSVG = el.querySelector("svg[viewBox]") !== null;

    if (isSVG) {
      const encodedSVG = encodeURIComponent(el.innerHTML.trim());
      data.src = `data:image/svg+xml,${encodedSVG}`;
      data.inlineSVG = true;
      width = (el.clientWidth || 400) * 10;
      height = (el.clientHeight || 300) * 10;

      // 1x1 placeholder to prevent background flicker on inline SVGs
      data.msrc =
        "data:image/svg+xml,%3Csvg%20xmlns%3D%27http%3A//www.w3.org/2000/svg%27%20width%3D%271%27%20height%3D%271%27/%3E";
    }

    const imgInfo = el.querySelector(".informations")?.textContent || "";
    const imgEl = el.tagName === "IMG" ? el : el.querySelector("img");

    if (!width || !height) {
      const dimensions = imgInfo.trim().split(" ")[0];
      [width, height] = dimensions.split(/x|×/).map(Number);
    }

    // this ensures that cropped images (eg: grid) do not cause jittering when closing
    data.thumbCropped = true;

    data.src ||= el.getAttribute("data-large-src");
    data.msrc ||= imgEl?.currentSrc || imgEl?.src;
    data.origSrc =
      imgEl?.getAttribute("data-orig-src") ||
      el.getAttribute("data-orig-src") ||
      null;
    data.title = el.title || imgEl?.alt || imgEl?.title || null;
    data.aiDescription = imgEl?.getAttribute("aria-description") || null;
    data.base62SHA1 =
      imgEl?.getAttribute("data-base62-sha1") ||
      el.getAttribute("data-base62-sha1") ||
      null;
    data.details = imgInfo;
    data.w = data.width = width;
    data.h = data.height = height;
    data.targetWidth =
      el.getAttribute("data-target-width") ||
      imgEl?.getAttribute("width") ||
      null;
    data.targetHeight =
      el.getAttribute("data-target-height") ||
      imgEl?.getAttribute("height") ||
      null;

    // If thumbnail has different aspect ratio than full image,
    // use the full image as msrc for smooth zoom animation
    if (data.targetWidth && data.targetHeight && data.w && data.h) {
      const thumbRatio = data.targetWidth / data.targetHeight;
      const fullRatio = data.w / data.h;
      if (Math.abs(thumbRatio - fullRatio) / fullRatio > 0.05) {
        data.msrc = data.src;
      }
    }

    // So we can attach things like a Post model from the caller.
    Object.keys(additionalData).forEach((key) => {
      data[key] = additionalData[key];
    });

    return data;
  });

  const itemsToPreload = items.filter((item) => {
    const { largeSrc, targetWidth, targetHeight } = item.dataset;
    const hasImageSrc = largeSrc || item.getAttribute("href");
    const missingDimensions = !targetWidth || !targetHeight;
    const imgDimensions = item
      .querySelector(".informations")
      ?.textContent.trim()
      .split(" ")[0];
    const missingMetaData = !imgDimensions?.split(/x|×/).every((d) => !!d);

    return hasImageSrc && missingDimensions && missingMetaData;
  });

  await Promise.all(
    itemsToPreload.map(
      (item) =>
        new Promise((resolve) => {
          const img = new Image();
          img.src =
            item.getAttribute("data-large-src") || item.getAttribute("href");
          img.onload = () => {
            item.setAttribute("data-target-width", img.naturalWidth);
            item.setAttribute("data-target-height", img.naturalHeight);
            resolve();
          };
          img.onerror = resolve;
        })
    )
  );
  function paddingFn(viewportSize, itemData) {
    if (viewportSize.x < 1200 || caps.isMobileDevice) {
      return { top: 0, bottom: 0, left: 0, right: 0 };
    }
    return {
      top: 20,
      bottom: itemData.title ? 75 : 20,
      left: 20,
      right: 20,
    };
  }

  lightboxEl.init();
}

/**
 * Opens a lightbox over standalone media elements, for content that isn't
 * rendered as `.lightbox` links (editor images, pending uploads).
 *
 * @param {Array<{type?: "image"|"video", src: string, element?: HTMLImageElement|HTMLVideoElement, alt?: string}>} items
 *   `alt` labels the slide and its caption; images default to the element's alt
 * @param {{index?: number}} [options]
 * @returns {Promise<void>} resolves when the lightbox closes, or right away
 *   when another lightbox is already open
 */
export async function openLightbox(items, { index = 0 } = {}) {
  const dataSource = items.map(buildSlideData);

  // loaded up front: with a lazily loaded core, two quick opens race and one never settles
  const [{ default: PhotoSwipeLightbox }, { default: PhotoSwipe }] =
    await waitForPromise(
      Promise.all([
        import(
          /* dynamicChunkName: "photoswipe-lightbox" */ "photoswipe/lightbox"
        ),
        import(/* dynamicChunkName: "photoswipe" */ "photoswipe"),
      ])
    );
  const lightboxEl = new PhotoSwipeLightbox({
    ...sharedOptions(() => lightboxEl.pswp),
    dataSource,
    pswpModule: PhotoSwipe,
  });

  closeOnEscape(lightboxEl);

  lightboxEl.on("uiRegister", function () {
    lightboxEl.pswp.ui.registerElement({
      name: "caption",
      order: 11,
      isButton: false,
      appendTo: "root",
      html: "",
      onInit: (caption, pswp) => {
        const titleEl = document.createElement("div");
        titleEl.className = "pswp__caption-title";
        caption.appendChild(titleEl);

        pswp.on("change", () => {
          titleEl.textContent = pswp.currSlide?.data.alt || "";
        });
      },
    });
  });

  lightboxEl.on("contentLoad", (event) => {
    const { content } = event;
    if (content.type !== "video") {
      return;
    }

    event.preventDefault();

    const video = document.createElement("video");
    video.className = "pswp__video";
    video.src = content.data.src;
    video.controls = true;
    video.playsInline = true;
    video.preload = "metadata";
    if (content.data.alt) {
      video.setAttribute("aria-label", content.data.alt);
    }
    video.addEventListener(
      "loadedmetadata",
      () => syncSlideSize(content, video.videoWidth, video.videoHeight),
      { once: true }
    );
    // native controls swallow key and pointer events while focused, so Escape
    // would never reach the lightbox; media events are all they let through
    const refocus = () => video.focus({ preventScroll: true });
    for (const type of ["play", "pause", "seeked", "volumechange"]) {
      video.addEventListener(type, refocus);
    }
    content.element = video;
  });

  lightboxEl.on("contentLoadImage", ({ content }) => {
    const image = content.element;
    image.addEventListener(
      "load",
      () => syncSlideSize(content, image.naturalWidth, image.naturalHeight),
      { once: true }
    );
  });

  lightboxEl.on("contentDeactivate", ({ content }) => {
    if (content.type === "video") {
      content.element?.pause();
    }
  });

  lightboxEl.on("pointerDown", (event) => {
    if (event.originalEvent.target.closest?.(".pswp__video")) {
      event.preventDefault();
    }
  });

  return new Promise((resolve) => {
    lightboxEl.addFilter("thumbEl", (thumbEl, itemData) =>
      itemData.element?.isConnected ? itemData.element : undefined
    );
    lightboxEl.on("close", resolve);
    lightboxEl.on("closingAnimationEnd", () => lightboxEl.destroy());

    lightboxEl.init();
    if (!lightboxEl.loadAndOpen(index)) {
      lightboxEl.destroy();
      resolve();
    }
  });
}

function sharedOptions(getPswp) {
  return {
    arrowPrevTitle: i18n("lightbox.previous"),
    arrowNextTitle: i18n("lightbox.next"),
    closeTitle: i18n("lightbox.close"),
    zoomTitle: i18n("lightbox.zoom"),
    errorMsg: i18n("lightbox.error"),
    showHideAnimationType: isTesting() || isRailsTesting() ? "none" : "zoom",
    escKey: false,
    tapAction: (pt, event) => {
      if (event.target.closest(".pswp__video")) {
        return;
      }

      const pswp = getPswp();
      if (event.target.classList.contains("pswp__img")) {
        pswp?.element?.classList.toggle("pswp--ui-visible");
      } else {
        pswp?.close();
      }
    },
    pswpModule: () =>
      waitForPromise(import(/* dynamicChunkName: "photoswipe" */ "photoswipe")),
    appendToEl: isTesting() && document.getElementById("ember-testing"),
  };
}

// keeps Escape from also reaching handlers underneath the overlay
function closeOnEscape(lightboxEl) {
  const onKeyDown = (event) => {
    if (event.key !== "Escape") {
      return;
    }

    event.stopPropagation();
    event.preventDefault();
    lightboxEl.pswp.close();
  };

  lightboxEl.on("afterInit", () => {
    lightboxEl.pswp.element.addEventListener("keydown", onKeyDown);
  });
}

// initial sizes come from thumbnails, which may not have loaded yet
function syncSlideSize(content, width, height) {
  if (!width || !height) {
    return;
  }
  if (content.width === width && content.height === height) {
    return;
  }

  content.width = content.data.width = width;
  content.height = content.data.height = height;

  if (content.slide) {
    content.slide.width = width;
    content.slide.height = height;
    content.slide.resize();
    // resize() skips this when the slide is zoomed in
    content.slide.updateContentSize(true);
  }
}

function buildSlideData({ type = "image", src, element, alt }) {
  if (type === "video") {
    return {
      type,
      src,
      element,
      alt: alt || "",
      width: element?.videoWidth || 1280,
      height: element?.videoHeight || 720,
    };
  }

  return {
    src,
    msrc: element?.currentSrc || src,
    element,
    width: element?.naturalWidth || 800,
    height: element?.naturalHeight || 600,
    alt: alt ?? (element?.alt || ""),
    thumbCropped: true,
  };
}
